import { useState } from "react";
import { toast } from "sonner";
import { Star, Send } from "lucide-react";
import { supabase } from "@/lib/supabase";

export default function ReviewForm() {
  const [form, setForm] = useState({ author: "", role: "", comment: "" });
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.author || !form.comment) {
      toast.error("Merci d'indiquer votre nom et votre avis.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.from("reviews").insert({
        author: form.author,
        role: form.role || null,
        rating,
        comment: form.comment,
        source: "site",
        status: "pending",
      });
      if (error) throw error;
      toast.success("Merci ! Votre avis sera publié après validation.");
      setForm({ author: "", role: "", comment: "" });
      setRating(5);
    } catch {
      toast.error("Une erreur est survenue. Réessayez dans un instant.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative py-16 border-t border-white/5" data-testid="review-form-section">
      <div className="max-w-2xl mx-auto px-6 lg:px-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">/ Votre avis</div>
        <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-2">Laissez votre avis</h2>
        <p className="text-white/55 text-sm mb-8">
          Partagez votre expérience. Votre avis sera publié sur le site après une rapide validation.
        </p>

        <form onSubmit={submit} className="space-y-4 p-6 lg:p-8 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Nom *</label>
              <input
                value={form.author}
                onChange={change("author")}
                data-testid="reviewform-author"
                className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 text-white focus:outline-none focus:border-[#E50914] transition-colors"
                placeholder="Votre nom"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Prestation (optionnel)</label>
              <input
                value={form.role}
                onChange={change("role")}
                data-testid="reviewform-role"
                className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 text-white focus:outline-none focus:border-[#E50914] transition-colors"
                placeholder="Ex : Pack complet — Clio"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Note</label>
            <div className="flex items-center gap-1" data-testid="reviewform-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                  className="p-1"
                >
                  <Star
                    className={`w-7 h-7 transition-colors ${
                      n <= (hover || rating) ? "fill-[#E50914] text-[#E50914]" : "text-white/25"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Votre avis *</label>
            <textarea
              value={form.comment}
              onChange={change("comment")}
              rows="4"
              data-testid="reviewform-comment"
              className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 text-white resize-none focus:outline-none focus:border-[#E50914] transition-colors"
              placeholder="Racontez votre expérience…"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="reviewform-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] disabled:opacity-60 text-white font-bold uppercase text-sm tracking-wider px-6 py-4 rounded-lg border border-[#E50914] hover:shadow-[0_0_32px_rgba(229,9,20,0.5)] transition-all"
          >
            <Send className="w-4 h-4" /> {loading ? "Envoi…" : "Envoyer mon avis"}
          </button>
        </form>
      </div>
    </section>
  );
}
